import React, { useContext, useEffect, useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import '../css/HomePage.css'
import { logout } from '../actions/userActions'
import { ToastContext } from '../contexts/ToastContext.js'
import backendAPI from '../utils/backendAPI'
import { businessRowToProfileFields, getMerchantCheckoutContext } from '../utils/merchantContext'
import ProfileDropdown from './ProfileDropdown'
import MerchantSidebarMenu from './MerchantSidebarMenu'

/** Same left sidebar + top header shell as `HomePage` (not MainLayout / SideBar). */
function KlerkMerchantShell({ pageTitle, storeLabel = 'Your store', children }) {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const showToast = useContext(ToastContext)
	const { userInfo } = useSelector((state) => state.userLogin)
	const checkoutCtx = useMemo(() => getMerchantCheckoutContext(userInfo), [userInfo])
	const [resolvedStoreLabel, setResolvedStoreLabel] = useState(storeLabel)

	useEffect(() => {
		const fromStorage = String(localStorage.getItem('business_name') || '').trim()
		const propLabel = String(storeLabel || '').trim()
		const safePropLabel = propLabel && propLabel !== 'Your store' ? propLabel : ''
		const fallback =
			fromStorage ||
			safePropLabel ||
			checkoutCtx.businessName ||
			userInfo?.email ||
			'Your store'
		setResolvedStoreLabel(fallback)
	}, [checkoutCtx.businessName, storeLabel, userInfo?.email])

	useEffect(() => {
		let cancelled = false
		;(async () => {
			try {
				const res = await backendAPI.get('/api/user-businesses/')
				if (!cancelled && Array.isArray(res.data) && res.data.length > 0) {
					const fields = businessRowToProfileFields(res.data[0])
					if (fields?.business_name) {
						setResolvedStoreLabel(fields.business_name)
					}
				}
			} catch {
				// Keep fallback label if profile fetch is unavailable.
			}
		})()
		return () => {
			cancelled = true
		}
	}, [])

	const logoutHandler = () => {
		showToast?.('You have been logged out successfully.', 'info')
		dispatch(logout())
		navigate('/login', { replace: true })
	}

	return (
		<div className="klerk-app-container">
			<div className="klerk-sidebar">
				<div className="klerk-logo">Klerk</div>
				<MerchantSidebarMenu />
			</div>

			<div className="klerk-main-content">
				<div className="klerk-header d-flex justify-content-between align-items-center mb-4">
					<h4 className="page-title m-0">{pageTitle}</h4>

					<div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
						<div className="user-profile-widget d-flex align-items-center">
							<NavLink to="/merchant-profile/" className="store-name">
								{resolvedStoreLabel}{' '}
								<span className="wide-v">v</span>
							</NavLink>
							<span className="status-badge">{t('profile.active')}</span>
							<ProfileDropdown />
						</div>
						<button
							type="button"
							className="klerk-btn btn-secondary klerk-header-logout"
							onClick={logoutHandler}
						>
							{t('profile.logOut')}
						</button>
					</div>
				</div>

				{children}
			</div>
		</div>
	)
}

export default KlerkMerchantShell
